/**
 * Landing-URL marketing-param capture, ported from Ember's
 * `signup.captureLandingUrlParams`. The captured string feeds the startup
 * application's `url_params` attribution field, and `utm_partner` pre-fills
 * (and hides) the startup form's incubator/VC dropdown.
 *
 * Module-level memory, like Ember's service fields: the capture must survive
 * step navigation and page-internal remounts, but a full reload with fresh
 * marketing params re-captures (last-touch).
 */

// Query params the funnel carries in the URL across wizard steps. A re-entry
// carrying only these is not a fresh marketing landing and must not overwrite
// params captured at landing.
const STICKY_SIGNUP_PARAM_KEYS = new Set(['signupType', 'signupFlow', 'promoCode', 'content']);

let landingUrlParams: string | null = null;
let utmPartner: string | null = null;

/**
 * ⚠️ Feed this raw query-string pairs — `Object.fromEntries(new
 * URLSearchParams(window.location.search))` — never TanStack Router's parsed
 * search object. The router's default `parseSearch` runs every value through
 * `JSON.parse` (see `signup-search.ts`), which is lossy for attribution:
 * `?utm_campaign=00123` becomes the number `123`, and numeric-valued
 * marketing params (`campaignid`, `adgroupid`, …) stop being strings. The
 * string-only signature makes the parsed-search object a compile error
 * instead of a silent drop.
 */
export function captureLandingUrlParams(queryParams: Record<string, string>): void {
  const params = new URLSearchParams();
  let hasMarketingParams = false;
  for (const [key, value] of Object.entries(queryParams)) {
    if (typeof value === 'string' && value !== '') {
      params.set(key, value);
      if (!STICKY_SIGNUP_PARAM_KEYS.has(key)) {
        hasMarketingParams = true;
      }
    }
  }

  // Once a landing set is captured, a later re-entry carrying only sticky
  // params must not clobber the marketing params from the original landing. A
  // re-entry with fresh marketing params still overwrites (last-touch).
  if (!hasMarketingParams && landingUrlParams != null) {
    return;
  }

  const serialized = params.toString();
  landingUrlParams = serialized === '' ? '' : `?${serialized}`;

  // `utm_partner` is a marketing param, so this branch only runs on a fresh
  // marketing landing (the early return above preserves a prior capture on
  // sticky-only re-entry). Set when present, clear when absent so a new
  // landing without it doesn't inherit a stale value.
  const partner = queryParams.utm_partner;
  utmPartner = typeof partner === 'string' && partner !== '' ? partner : null;
}

export function getLandingUrlParams(): string {
  return landingUrlParams ?? '';
}

export function getUtmPartner(): string | null {
  return utmPartner;
}

/** Test-only: forget any capture from a previous test. */
export function resetLandingUrlParamsForTesting(): void {
  landingUrlParams = null;
  utmPartner = null;
}
